import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, Save, Loader2, Trophy, CalendarCheck } from 'lucide-react';
import axios from 'axios';
import { getToken, clearToken, me } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSeason() {
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [targetDate, setTargetDate] = useState('');
  const [label, setLabel] = useState('');
  const [visibleFrom, setVisibleFrom] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  useEffect(() => {
    if (!authOk) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/season`);
        setTargetDate(data.targetDate);
        setLabel(data.label);
        setVisibleFrom(data.visibleFrom || '2027-01-01');
        setUpdatedAt(data.updatedAt);
      } catch (e) {
        toast({ title: 'Error loading', description: e.response?.data?.detail || e.message });
      } finally { setLoading(false); }
    })();
  }, [authOk, toast]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/season`, { targetDate, label, visibleFrom }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUpdatedAt(data.updatedAt);
      toast({ title: 'Season countdown updated' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  const targetLabel = targetDate ? new Date(`${targetDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div>
              <div className="font-display text-2xl leading-none mt-1">SEASON COUNTDOWN</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/scholars" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Scholars</Link>
            <Link to="/admin/orders" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Orders</Link>
            <Link to="/admin/inventory" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Inventory</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Change Password</Link>
            <button
              onClick={() => { clearToken(); window.location.href = '/admin/login'; }}
              className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20"
            >
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-10">
        {loading ? (
          <div className="p-16 text-center text-[#0a1f44]/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <form onSubmit={save} className="bg-white border border-[#0a1f44]/10 p-8 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-[#c8102e] flex items-center justify-center text-white">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Season Kickoff</div>
                <div className="font-display text-[#0a1f44] text-2xl">Live Home Page Countdown</div>
              </div>
            </div>
            <p className="text-sm text-[#0a1f44]/60">
              This target date controls the big countdown block on the Home page. Update it once per season (e.g., January 1 of each new year) and every visitor sees the new date instantly — no rebuild required.
            </p>

            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Kickoff Date</label>
              <input
                type="date"
                required
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                data-testid="season-form-date"
                className="w-full border border-[#0a1f44]/20 px-3 py-3 text-lg focus:outline-none focus:border-[#c8102e]"
              />
              {targetDate && (
                <div className="mt-2 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60 flex items-center gap-1" data-testid="season-form-preview">
                  <CalendarCheck className="w-3 h-3 text-[#c8102e]" /> Preview: {targetLabel}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Countdown Label</label>
              <input
                type="text"
                required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="season-form-label"
                className="w-full border border-[#0a1f44]/20 px-3 py-3 focus:outline-none focus:border-[#c8102e]"
              />
              <div className="mt-1 text-[10px] text-[#0a1f44]/50">e.g. "2027 Patriots Season Kickoff", "Fall Practice Begins", "First Game of the Year"</div>
            </div>

            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Show Countdown From</label>
              <input
                type="date"
                required
                value={visibleFrom}
                onChange={(e) => setVisibleFrom(e.target.value)}
                data-testid="season-form-visible-from"
                className="w-full border border-[#0a1f44]/20 px-3 py-3 focus:outline-none focus:border-[#c8102e]"
              />
              <div className="mt-1 text-[10px] text-[#0a1f44]/50">The countdown block stays hidden on the Home page until this date arrives, then it appears automatically.</div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-[#0a1f44]/10">
              <div className="text-xs text-[#0a1f44]/50 font-heading uppercase tracking-widest">
                {updatedAt ? `Last updated: ${new Date(updatedAt).toLocaleDateString()}` : ''}
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="season-form-save"
                className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-xs px-6 py-3 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Countdown
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
